/**
 * ProcessSteps — recorrido de un proceso por pasos (MVP-02B §7).
 *
 * Patrón genérico del design system: explica en qué punto está algo, quién
 * tiene que actuar y qué falta. Lo usan el propietario (recorrido comercial
 * del producto digital) y la administración (revisión del paquete), para que
 * ambos lean el mismo estado con el mismo lenguaje.
 *
 * Estados por paso:
 *   done     — completado
 *   current  — es el paso en curso
 *   blocked  — no puede avanzar; `reason` explica por qué
 *   pending  — todavía no le toca
 *
 * Props:
 *   steps        — [{ id, label, description?, state, reason?, actor?, action? }]
 *                  `action` = { label, onClick } para el CTA del paso
 *   orientation  — "vertical" (default) | "horizontal"
 *   compact      — reduce tipografía y espaciado
 */
import PropTypes from "prop-types";
import { AlertTriangle, Check, Circle, Loader2, Lock } from "lucide-react";

const STEP_STYLES = {
    done: {
        marker: "bg-success/15 text-success border-success/40",
        label: "text-base-content",
        icon: Check,
    },
    current: {
        marker: "bg-primary/15 text-primary border-primary/50",
        label: "text-base-content font-semibold",
        icon: Loader2,
    },
    blocked: {
        marker: "bg-warning/15 text-warning border-warning/50",
        label: "text-base-content",
        icon: AlertTriangle,
    },
    pending: {
        marker: "bg-base-300/40 text-base-content/40 border-base-300",
        label: "text-base-content/50",
        icon: Circle,
    },
};

const ESTADO_TEXTO = {
    done: "Completado",
    current: "En curso",
    blocked: "Bloqueado",
    pending: "Pendiente",
};

const ProcessSteps = ({ steps = [], orientation = "vertical", compact = false }) => {
    const horizontal = orientation === "horizontal";

    return (
        <ol
            className={
                horizontal
                    ? "flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-0"
                    : "flex flex-col gap-3"
            }
        >
            {steps.map((step, index) => {
                const style = STEP_STYLES[step.state] ?? STEP_STYLES.pending;
                const Icon = step.state === "pending" ? Lock : style.icon;
                const isLast = index === steps.length - 1;

                return (
                    <li
                        key={step.id ?? step.label}
                        className={horizontal ? "flex-1 flex sm:flex-col gap-3 sm:gap-2" : "flex gap-3"}
                        aria-current={step.state === "current" ? "step" : undefined}
                    >
                        {/* Marcador + línea de conexión */}
                        <div
                            className={
                                horizontal
                                    ? "flex sm:flex-row flex-col items-center gap-2 sm:w-full"
                                    : "flex flex-col items-center"
                            }
                        >
                            <span
                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${style.marker}`}
                            >
                                <Icon
                                    size={14}
                                    className={step.state === "current" ? "animate-spin" : undefined}
                                    aria-hidden="true"
                                />
                            </span>
                            {!isLast && (
                                <span
                                    className={
                                        horizontal
                                            ? "hidden sm:block h-px flex-1 bg-base-300"
                                            : "w-px flex-1 bg-base-300 my-1"
                                    }
                                    aria-hidden="true"
                                />
                            )}
                        </div>

                        {/* Contenido */}
                        <div className={`min-w-0 ${horizontal ? "sm:pr-4 pb-1" : "pb-2"} flex-1`}>
                            <p className={`${compact ? "text-xs" : "text-sm"} ${style.label}`}>
                                {step.label}
                            </p>
                            {/* Estado en texto: el color por sí solo no es accesible. */}
                            <span className="sr-only">{ESTADO_TEXTO[step.state] ?? ""}</span>

                            {step.description && (
                                <p className={`${compact ? "text-[11px]" : "text-xs"} text-base-content/60 mt-0.5`}>
                                    {step.description}
                                </p>
                            )}
                            {step.reason && (
                                <p className={`${compact ? "text-[11px]" : "text-xs"} text-warning mt-0.5`}>
                                    {step.reason}
                                </p>
                            )}
                            {step.actor && (
                                <p className={`${compact ? "text-[11px]" : "text-xs"} text-base-content/50 mt-0.5`}>
                                    {step.actor}
                                </p>
                            )}
                            {step.action && (
                                <button
                                    type="button"
                                    onClick={step.action.onClick}
                                    className="btn btn-xs btn-outline mt-2"
                                >
                                    {step.action.label}
                                </button>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
};

ProcessSteps.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string.isRequired,
            description: PropTypes.string,
            state: PropTypes.oneOf(["done", "current", "blocked", "pending"]).isRequired,
            reason: PropTypes.string,
            actor: PropTypes.string,
            action: PropTypes.shape({
                label: PropTypes.string.isRequired,
                onClick: PropTypes.func.isRequired,
            }),
        }),
    ).isRequired,
    orientation: PropTypes.oneOf(["vertical", "horizontal"]),
    compact: PropTypes.bool,
};

export default ProcessSteps;
